import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { SuiCheckbox, SuiTextInput, SuiContainer } from '@splashthat/sui';

import { getFormNode } from 'common/entities/formNode/selectors';
import { updateFormNodeConfigValue } from '../../../actions/nodeActions';
import disableWhenLocked from './disableWhenLocked';

import { makeValidAttachmentUrl, RESOURCE_URL_PLACEHOLDER } from '../../../utils/urlValidatiorHelper';

const CHECKBOX_LABEL = 'Show attachment link';
const RESOURCE_URL_LABEL = 'Resource URL';
const RESOURCE_LABEL_LABEL = 'Resource Label';
const RESOURCE_LABEL_PLACEHOLDER = '(view)';

/**
 * UI piece to customize a resource to be linked from in Checkbox questions.
 * Most common use case is linking to a PDF with terms & conditions.
 */
class CheckboxAttachmentControl extends Component {
    static propTypes = {
        linkUrl: PropTypes.string,
        linkLabel: PropTypes.string,
        /** disabled is passed when form node editing is locked */
        disabled: PropTypes.bool.isRequired,
        onChangeLinkUrl: PropTypes.func.isRequired,
        onChangeLinkLabel: PropTypes.func.isRequired,
    };

    static defaultProps = {
        linkUrl: '',
        linkLabel: '',
    };

    state = {
        enabled: this.props.linkUrl !== '',
        previousLinkUrl: undefined,
        previousLinkLabel: RESOURCE_LABEL_PLACEHOLDER,
    };

    handleToggle = (event, value) => {
        if (!value) {
            // Save in state previous values before deactivation.
            this.setState({
                enabled: false,
                previousLinkUrl: this.props.linkUrl,
                previousLinkLabel: this.props.linkLabel,
            });
            // Call action creators to remove url and label from entity.
            this.props.onChangeLinkUrl(undefined);
            this.props.onChangeLinkLabel(undefined);
        } else {
            // Call action cretors to restore url and label from previouos values.
            this.props.onChangeLinkUrl(this.state.previousLinkUrl);
            this.props.onChangeLinkLabel(this.state.previousLinkLabel);
            // Update state by clearing out any previous values to this activation.
            this.setState({
                enabled: true,
                previousLinkUrl: undefined,
                previousLinkLabel: undefined,
            });
        }
    };

    handleChangeLinkUrl = (event, value) => {
        this.props.onChangeLinkUrl(value);
    };

    handleChangeLinkLabel = (event, value) => {
        this.props.onChangeLinkLabel(value);
    };

    handleOnBlurLinkUrl = () => {
        const { linkUrl } = this.props;
        if (linkUrl) {
            const validLinkUrl = makeValidAttachmentUrl(linkUrl);
            this.props.onChangeLinkUrl(validLinkUrl);
        }
    };

    render() {
        return (
            <SuiContainer variation="flex-vertical flex-inherit">
                <SuiContainer variation="flex-group">
                    <SuiCheckbox
                        label={CHECKBOX_LABEL}
                        checked={this.state.enabled}
                        onChange={this.handleToggle}
                        htmlType="url"
                        disabled={this.props.disabled}
                    />
                </SuiContainer>

                {this.state.enabled && (
                    <SuiContainer variation="flex-group flex-horizontal flex-inherit flex-groups-grow">
                        <SuiContainer variation="flex-group">
                            <SuiTextInput
                                type="url"
                                label={RESOURCE_URL_LABEL}
                                placeholder={RESOURCE_URL_PLACEHOLDER}
                                value={this.props.linkUrl}
                                onChange={this.handleChangeLinkUrl}
                                fullWidth
                                onBlur={this.handleOnBlurLinkUrl}
                                disabled={this.props.disabled}
                            />
                        </SuiContainer>

                        <SuiContainer variation="flex-group">
                            <SuiTextInput
                                label={RESOURCE_LABEL_LABEL}
                                placeholder={RESOURCE_LABEL_PLACEHOLDER}
                                value={this.props.linkLabel}
                                onChange={this.handleChangeLinkLabel}
                                fullWidth
                                disabled={this.props.disabled}
                            />
                        </SuiContainer>
                    </SuiContainer>
                )}
            </SuiContainer>
        );
    }
}

// -------------------
// Connected component

const mapStateToProps = (state, props) => {
    const formNode = getFormNode(state, props.formNodeId);
    return {
        linkUrl: props.formNodeId ? formNode.config.linkUrl : '',
        linkLabel: props.formNodeId ? formNode.config.linkLabel : '',
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    onChangeLinkUrl: (linkUrl) => dispatch(updateFormNodeConfigValue(ownProps.formNodeId, 'linkUrl', linkUrl)),
    onChangeLinkLabel: (linkLabel) => dispatch(updateFormNodeConfigValue(ownProps.formNodeId, 'linkLabel', linkLabel)),
});

const ConnectedCheckboxAttachmentControl = connect(
    mapStateToProps,
    mapDispatchToProps,
)(disableWhenLocked(CheckboxAttachmentControl));

ConnectedCheckboxAttachmentControl.propTypes = {
    formNodeId: PropTypes.string,
};
ConnectedCheckboxAttachmentControl.defaultProps = {
    formNodeId: undefined,
};

export default ConnectedCheckboxAttachmentControl;
